import { File as FileIcon, X, ShieldCheck } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ChatAttachment } from "@researcher/shared";

interface MessageAttachmentsProps {
  attachments: ChatAttachment[];
  /** Optional pre-resolved previewUrls (object URLs) for just-uploaded files. */
  previewUrls?: Record<string, string>;
}

/**
 * Renders a compact attachment row inside the user's message bubble. Image
 * previews show inline and tap to enlarge; PDFs show as a labeled chip. Once
 * the 48h TTL passes the Storage object is gone — we don't try to refetch and
 * just degrade to the filename chip.
 */
export default function MessageAttachments({ attachments, previewUrls }: MessageAttachmentsProps) {
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [showRetentionInfo, setShowRetentionInfo] = useState(false);

  useEffect(() => {
    if (!lightbox && !showRetentionInfo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightbox(null);
        setShowRetentionInfo(false);
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, showRetentionInfo]);

  if (attachments.length === 0) return null;

  return (
    <>
      <div className="flex flex-wrap gap-2 mt-2">
        {attachments.map((att) => {
          const isImage = att.mimeType.startsWith("image/");
          const localPreview = previewUrls?.[att.storagePath];

          if (isImage && localPreview) {
            return (
              <button
                key={att.storagePath}
                type="button"
                onClick={() => setLightbox({ src: localPreview, alt: att.filename })}
                className="p-0 border-0 bg-transparent cursor-zoom-in"
                aria-label={`Expand ${att.filename}`}
              >
                <img
                  src={localPreview}
                  alt={att.filename}
                  className="max-w-[180px] max-h-[180px] rounded-lg border border-pink-200/60 dark:border-pink-800/60"
                />
              </button>
            );
          }

          // PDF or image-after-TTL chip. Both are tappable for the retention
          // explainer — the user has every reason to wonder "why can't I see
          // this anymore?" and we want to answer that as a trust signal.
          return (
            <button
              key={att.storagePath}
              type="button"
              onClick={() => setShowRetentionInfo(true)}
              className="flex items-center gap-2 px-2 py-1.5 bg-white/60 dark:bg-gray-800/60 border border-pink-200/60 dark:border-pink-800/60 rounded-lg text-xs hover:bg-white dark:hover:bg-gray-800 transition-colors"
              aria-label={`${att.filename} — image removed after 48 hours, tap for details`}
            >
              <FileIcon size={14} weight="fill" className="text-pink-500 flex-shrink-0" />
              <span className="truncate max-w-[140px] text-gray-700 dark:text-gray-200">
                {att.filename}
              </span>
            </button>
          );
        })}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Enlarged view: ${lightbox.alt}`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 z-10 p-3 rounded-full bg-black/70 hover:bg-black/90 text-white shadow-lg"
            style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
            aria-label="Close enlarged image"
          >
            <X size={24} weight="bold" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-w-full object-contain rounded-lg"
            style={{ maxHeight: 'calc(100vh - 5rem)' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {showRetentionInfo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setShowRetentionInfo(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Image retention policy"
        >
          <div
            className="relative bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowRetentionInfo(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Close"
            >
              <X size={16} weight="bold" />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={20} weight="fill" className="text-pink-500" />
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">Image removed after 48 hours</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
              Romy keeps uploaded images for 48 hours, then auto-deletes them. Anything Romy extracted from the image — lab values, medications, prescriptions — is saved separately to your health record and stays as long as you want it to.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              We delete the raw images on purpose: less stored data about your body, your labs, and your prescriptions means less to lose.
            </p>
            <div className="flex items-center justify-between">
              <Link
                to="/trust"
                onClick={() => setShowRetentionInfo(false)}
                className="text-sm text-pink-600 dark:text-pink-400 hover:underline"
              >
                Learn more →
              </Link>
              <button
                type="button"
                onClick={() => setShowRetentionInfo(false)}
                className="text-sm px-3 py-1.5 rounded-md bg-pink-500 hover:bg-pink-600 text-white font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
